import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    LinearProgress,
    Alert,
    Snackbar,
    Card,
    CardContent,
    Grid,
    Chip,
    IconButton,
    Stack,
    Divider,
    TextField,
    FormControlLabel,
    Switch,
    CircularProgress,
} from '@mui/material';
import { Close as CloseIcon, Send as SendIcon, Edit as EditIcon } from '@mui/icons-material';
import { useQuery, useMutation, gql } from '@apollo/client';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useParams } from 'react-router-dom';

const GET_EMAIL_TEMPLATES_FOR_STAND = gql`
    query GetEmailTemplatesForStand($eventId: String!) {
        getAllEmailTemplatesForStands(eventId: $eventId) {
            id
            role
            userId
            invitation_subject
            invitation_body
            invitation_is_html
            confirmation_subject
            confirmation_body
            confirmation_is_html
            accept_subject
            accept_body
            accept_is_html
            reject_subject
            reject_body
            reject_is_html
            created_at
            updated_at
        }
    }
`;

const GET_EMAIL_TEMPLATE_BY_ID = gql`
    query GetEmailTemplateById(
        $eventId: String!
        $templateId: String!
        $templateType: String!
        $useTemplateFrom: String!
    ) {
        getEmailTemplateById(
            eventId: $eventId
            templateId: $templateId
            templateType: $templateType
            useTemplateFrom: $useTemplateFrom
        ) {
            id
            role
            templateType
            subject
            body
            isHtml
            createdAt
            modified
        }
    }
`;

const SEND_EMAIL_TO_STAND = gql`
    mutation SendEmailToStand($input: SendEmailToStandInput!) {
        sendEmailToStand(input: $input)
    }
`;

export default function SendEmailToStand({ open, onClose, stand }) {
    const { eventId } = useParams();
    const [step, setStep] = useState(0); // 0: Select Template, 1: Review & Edit, 2: Sending
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateType, setTemplateType] = useState('');
    const [useTemplateFrom, setUseTemplateFrom] = useState('templates');
    const [subject, setSubject] = useState('');
    const [body, setBody] = useState('');
    const [isHtml, setIsHtml] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const { data: templatesData, loading: templatesLoading } = useQuery(GET_EMAIL_TEMPLATES_FOR_STAND, {
        variables: { eventId },
        skip: !open || !eventId,
    });

    const { data: templateData, loading: templateLoading } = useQuery(GET_EMAIL_TEMPLATE_BY_ID, {
        variables: {
            eventId,
            templateId: selectedTemplate?.id?.toString() || '',
            templateType: templateType,
            useTemplateFrom: useTemplateFrom,
        },
        skip: !selectedTemplate || !editMode,
        onCompleted: data => {
            if (data?.getEmailTemplateById) {
                const template = data.getEmailTemplateById;
                setSubject(template.subject || '');
                setBody(template.body || '');
                setIsHtml(template.isHtml !== undefined ? template.isHtml : true);
            }
        },
    });

    const [sendEmailToStand, { loading: sendingEmail }] = useMutation(SEND_EMAIL_TO_STAND, {
        onCompleted: data => {
            setSnackbar({ open: true, message: 'Email sent successfully!', severity: 'success' });
            setTimeout(() => {
                handleClose();
            }, 1500);
        },
        onError: error => {
            setSnackbar({ open: true, message: error.message || 'Failed to send email', severity: 'error' });
        },
    });

    const handleClose = () => {
        setStep(0);
        setSelectedTemplate(null);
        setSubject('');
        setBody('');
        setTemplateType('');
        setUseTemplateFrom('templates');
        setEditMode(false);
        if (onClose) onClose();
    };

    const handleTemplateSelect = (template, type) => {
        setSelectedTemplate(template);
        setTemplateType(type);
        setUseTemplateFrom('templates');

        // Extract subject and body based on template type
        let templateSubject = '';
        let templateBody = '';
        let templateIsHtml = true;

        switch (type) {
            case 'invitation':
                templateSubject = template.invitation_subject || '';
                templateBody = template.invitation_body || '';
                templateIsHtml = template.invitation_is_html !== undefined ? template.invitation_is_html : true;
                break;
            case 'confirmation':
                templateSubject = template.confirmation_subject || '';
                templateBody = template.confirmation_body || '';
                templateIsHtml = template.confirmation_is_html !== undefined ? template.confirmation_is_html : true;
                break;
            case 'accept':
                templateSubject = template.accept_subject || '';
                templateBody = template.accept_body || '';
                templateIsHtml = template.accept_is_html !== undefined ? template.accept_is_html : true;
                break;
            case 'reject':
                templateSubject = template.reject_subject || '';
                templateBody = template.reject_body || '';
                templateIsHtml = template.reject_is_html !== undefined ? template.reject_is_html : true;
                break;
            default:
                templateSubject = template.invitation_subject || '';
                templateBody = template.invitation_body || '';
                templateIsHtml = template.invitation_is_html !== undefined ? template.invitation_is_html : true;
        }

        setSubject(templateSubject);
        setBody(templateBody);
        setIsHtml(templateIsHtml);
        setStep(1);
    };

    const handleSend = () => {
        if (!subject || !body) {
            setSnackbar({ open: true, message: 'Please fill in subject and body', severity: 'error' });
            return;
        }

        sendEmailToStand({
            variables: {
                input: {
                    eventId,
                    standId: stand.id,
                    templateId: selectedTemplate?.id?.toString(),
                    templateType: templateType,
                    subject: subject,
                    body: body,
                    isHtml: isHtml,
                },
            },
        });
    };

    const templates = templatesData?.getAllEmailTemplatesForStands || [];

    // Group templates by type
    const groupedTemplates = {
        invitation: templates.filter(t => t.invitation_subject && t.invitation_body),
        confirmation: templates.filter(t => t.confirmation_subject && t.confirmation_body),
        accept: templates.filter(t => t.accept_subject && t.accept_body),
        reject: templates.filter(t => t.reject_subject && t.reject_body),
    };

    const getTemplateTypeLabel = type => {
        const labels = {
            invitation: 'Invitation',
            confirmation: 'Confirmation',
            accept: 'Accept',
            reject: 'Reject',
        };
        return labels[type] || type;
    };

    const getTemplateTypeColor = type => {
        const colors = {
            invitation: 'primary',
            confirmation: 'success',
            accept: 'info',
            reject: 'error',
        };
        return colors[type] || 'default';
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth='lg' fullWidth>
                <DialogTitle>
                    <Stack direction='row' justifyContent='space-between' alignItems='center'>
                        <Typography variant='h6'>Send Email to {stand?.company || 'Stand'}</Typography>
                        <IconButton onClick={handleClose}>
                            <CloseIcon />
                        </IconButton>
                    </Stack>
                </DialogTitle>
                <DialogContent>
                    {step === 0 && (
                        <Box sx={{ mt: 2 }}>
                            {templatesLoading ? (
                                <LinearProgress />
                            ) : templates.length === 0 ? (
                                <Alert severity='info'>
                                    No email templates available. Please create templates first.
                                </Alert>
                            ) : (
                                <Stack spacing={3}>
                                    {Object.entries(groupedTemplates).map(([type, typeTemplates]) => {
                                        if (typeTemplates.length === 0) return null;
                                        return (
                                            <Box key={type}>
                                                <Typography variant='subtitle1' sx={{ mb: 2, fontWeight: 600 }}>
                                                    {getTemplateTypeLabel(type)} Templates
                                                </Typography>
                                                <Grid container spacing={2}>
                                                    {typeTemplates.map(template => (
                                                        <Grid item xs={12} sm={6} key={template.id}>
                                                            <Card
                                                                sx={{
                                                                    cursor: 'pointer',
                                                                    transition: 'all 0.3s',
                                                                    '&:hover': {
                                                                        transform: 'translateY(-4px)',
                                                                        boxShadow: 4,
                                                                    },
                                                                }}
                                                                onClick={() => handleTemplateSelect(template, type)}
                                                            >
                                                                <CardContent>
                                                                    <Stack spacing={1}>
                                                                        <Stack
                                                                            direction='row'
                                                                            justifyContent='space-between'
                                                                            alignItems='center'
                                                                        >
                                                                            <Chip
                                                                                label={getTemplateTypeLabel(type)}
                                                                                color={getTemplateTypeColor(type)}
                                                                                size='small'
                                                                            />
                                                                        </Stack>
                                                                        <Typography
                                                                            variant='subtitle2'
                                                                            fontWeight={600}
                                                                        >
                                                                            {type === 'invitation' &&
                                                                                template.invitation_subject}
                                                                            {type === 'confirmation' &&
                                                                                template.confirmation_subject}
                                                                            {type === 'accept' &&
                                                                                template.accept_subject}
                                                                            {type === 'reject' &&
                                                                                template.reject_subject}
                                                                        </Typography>
                                                                        <Typography
                                                                            variant='caption'
                                                                            color='text.secondary'
                                                                            noWrap
                                                                        >
                                                                            {type === 'invitation' &&
                                                                                template.invitation_body?.substring(
                                                                                    0,
                                                                                    100
                                                                                )}
                                                                            {type === 'confirmation' &&
                                                                                template.confirmation_body?.substring(
                                                                                    0,
                                                                                    100
                                                                                )}
                                                                            {type === 'accept' &&
                                                                                template.accept_body?.substring(0, 100)}
                                                                            {type === 'reject' &&
                                                                                template.reject_body?.substring(0, 100)}
                                                                            ...
                                                                        </Typography>
                                                                    </Stack>
                                                                </CardContent>
                                                            </Card>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            </Box>
                                        );
                                    })}
                                </Stack>
                            )}
                        </Box>
                    )}

                    {step === 1 && (
                        <Box sx={{ mt: 2 }}>
                            <Stack spacing={3}>
                                {sendingEmail && (
                                    <Alert severity='info' icon={<CircularProgress size={20} />}>
                                        Sending email... Please wait.
                                    </Alert>
                                )}
                                {!sendingEmail && (
                                    <Alert severity='info'>
                                        You can edit the email content below. Variables from the stand data (like
                                        $company, $email, $stallNo, etc.) will be replaced automatically.
                                    </Alert>
                                )}
                                <TextField
                                    label='Subject'
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    fullWidth
                                    required
                                    disabled={sendingEmail}
                                />
                                <Box>
                                    <Typography variant='subtitle2' sx={{ mb: 1 }}>
                                        Email Body {isHtml ? '(HTML)' : '(Plain Text)'}
                                    </Typography>
                                    {isHtml ? (
                                        <Box
                                            sx={{
                                                opacity: sendingEmail ? 0.6 : 1,
                                                pointerEvents: sendingEmail ? 'none' : 'auto',
                                            }}
                                        >
                                            <ReactQuill
                                                theme='snow'
                                                value={body}
                                                onChange={setBody}
                                                style={{ height: '300px', marginBottom: '50px' }}
                                                readOnly={sendingEmail}
                                            />
                                        </Box>
                                    ) : (
                                        <TextField
                                            multiline
                                            rows={10}
                                            value={body}
                                            onChange={e => setBody(e.target.value)}
                                            fullWidth
                                            required
                                            disabled={sendingEmail}
                                        />
                                    )}
                                </Box>
                                <Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isHtml}
                                                onChange={e => setIsHtml(e.target.checked)}
                                                disabled={sendingEmail}
                                            />
                                        }
                                        label='HTML Mode'
                                    />
                                </Box>
                            </Stack>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={sendingEmail}>
                        Cancel
                    </Button>
                    {step === 0 && (
                        <Button variant='contained' disabled={templatesLoading || templates.length === 0}>
                            Next
                        </Button>
                    )}
                    {step === 1 && (
                        <>
                            <Button onClick={() => setStep(0)} disabled={sendingEmail}>
                                Back
                            </Button>
                            <Button
                                variant='contained'
                                startIcon={sendingEmail ? <CircularProgress size={16} color='inherit' /> : <SendIcon />}
                                onClick={handleSend}
                                disabled={sendingEmail || !subject || !body}
                            >
                                {sendingEmail ? 'Sending...' : 'Send Email'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </>
    );
}
